import useAudioStore from '@/shared/store/audio.store'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url'
import { useState, useCallback } from 'react'
import type { FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import useMediaStore from '@/shared/store/media.store.tsx'
import Spinner from '@/components/common/Spinner'
import { MediaFileService } from '@/features/media-file/data/media-file.service'
import Icon from '@/components/common/Icon'
import type { MouseEventHandler } from 'react'
import { getAudioBufferFromFile } from '@/utils/get-audio-buffer-from-file'

interface PlayButtonProps {
  soundId: string // Raw ID for API calls (UUID for media files, filename for files)
  audioUrl?: string
  file?: File | undefined
  audioSourceType?: 'media-file' | 'upload' | 'recording' | 'temp-file' // Type for audio orchestration ID
}

const PlayButton: FC<PlayButtonProps> = ({
  soundId,
  audioUrl,
  file,
  audioSourceType,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { addMedia, media } = useMediaStore()
  const { playAudioBuffer, isAudioPlaying, currentAudioId, stopPlayback } =
    useAudioStore(
      useShallow(
        ({
          playAudioBuffer,
          isPlaying: isAudioPlaying,
          currentAudioId,
          stopPlayback,
        }) => ({
          playAudioBuffer,
          isAudioPlaying,
          currentAudioId,
          stopPlayback,
        })
      )
    )

  const onClickPlay: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    if (isPlaying) {
      stopPlayback()
      return
    }
    fetchSound()
  }

  const fetchSound = useCallback(async () => {
    setIsLoading(true)

    // Generate orchestrator ID based on source type, fallback to raw soundId
    const orchestratorId = audioSourceType
      ? `${audioSourceType}:${soundId}`
      : soundId
    if (media[orchestratorId]) {
      playAudioBuffer(orchestratorId, media[orchestratorId], undefined, 0)
    } else {
      let bfr: AudioBuffer
      if (file) {
        bfr = await getAudioBufferFromFile(file)
      } else {
        const mediaUrl =
          audioUrl || (await MediaFileService.getFileUrl(soundId)) // Use raw soundId for API
        bfr = await getAudioBufferFromAudioUrl(mediaUrl)
      }
      if (bfr) {
        addMedia(orchestratorId, bfr) // Store with orchestrator ID
        playAudioBuffer(orchestratorId, bfr, undefined, 0) // Play with orchestrator ID
      }
    }
    setIsLoading(false)
  }, [
    soundId,
    audioUrl,
    audioSourceType,
    file,
    addMedia,
    playAudioBuffer,
    media,
  ])

  // Generate same orchestrator ID for isPlaying check
  const orchestratorId = audioSourceType
    ? `${audioSourceType}:${soundId}`
    : soundId
  const isPlaying = isAudioPlaying && currentAudioId === orchestratorId

  return (
    <button
      type="button"
      disabled={isLoading}
      className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium text-white transition-all duration-300 ${isPlaying ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white/[6%] hover:bg-white/[12%]'} `}
      onClick={onClickPlay}
    >
      {isLoading ? (
        <Spinner className="h-[20px] w-[20px]" />
      ) : isPlaying ? (
        <Icon name="stop" width={20} height={20} />
      ) : (
        <Icon name="play" width={20} height={20} />
      )}
    </button>
  )
}

export default PlayButton
